import apiClient from "@/api/apiClient";
import {
  createPaginatedRpcSupabaseQuery,
  createRpcQuery,
  createSearchRpcSupabaseQuery,
  spawnCreateApiFunction,
  spawnDeleteApiFunction,
  spawnGetApiFunction,
  spawnListInApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
} from "@/utils/api";

import type {
  GetPaginatedDeviceModelsProps,
  GetSearchDeviceModelsProps,
  OverrideReturnTypeGetDeviceModel,
  PaginatedDeviceModel,
} from "@/api/resources/deviceModels/types";

const table = "device_models";
const query = `*`;
export const apiConfigDeviceModels: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["name", "slug"],
  sortables: ["name", "slug", "created_at", "updated_at"],
};

export const getDeviceModel = spawnGetApiFunction({ table, query });
export const getDeviceModelsByIds = spawnListInApiFunction({ table, query });
export const createDeviceModel = spawnCreateApiFunction({ table, query });
export const updateDeviceModel = spawnUpdateApiFunction({ table, query });
export const deleteDeviceModel = spawnDeleteApiFunction({ table });

export const getDeviceModels = async (workspace_ids?: string[]) => {
  const { data, error } = await apiClient
    .rpc("get_device_models", { _workspace_ids: workspace_ids })
    .overrideTypes<OverrideReturnTypeGetDeviceModel[]>();
  if (error) throw error;
  return data;
};

export const getPaginatedDeviceModels = async ({
  workspace_ids,
  dataTableOptions,
  customFilters,
  customOrFilters,
}: GetPaginatedDeviceModelsProps) => {
  const { data, count, error } = await createPaginatedRpcSupabaseQuery<"get_device_models">({
    baseOptions: {
      rpcQuery: createRpcQuery(
        "get_device_models",
        {
          _workspace_ids: workspace_ids,
        },
        { count: "estimated" },
      ),
      searchables: apiConfigDeviceModels.searchables,
      sortables: apiConfigDeviceModels.sortables,
    },
    customFilters,
    customOrFilters,
    dataTableOptions,
  });
  if (error) throw error;
  const _data = data as PaginatedDeviceModel[] | null;
  return { data: _data, count };
};

export async function getDeviceModelsBySearch({
  workspace_ids,
  searchOptions,
  customFilters,
  customOrFilters,
  limit,
}: GetSearchDeviceModelsProps) {
  const { data, count, error } = await createSearchRpcSupabaseQuery<"get_device_models">({
    baseOptions: {
      rpcQuery: createRpcQuery("get_device_models", { _workspace_ids: workspace_ids }, { count: "exact" }),
      searchables: apiConfigDeviceModels.searchables,
      sortables: apiConfigDeviceModels.sortables,
    },
    searchOptions,
    customFilters,
    customOrFilters,
    limit,
  });

  if (error) throw error;
  const _data = data as PaginatedDeviceModel[] | null;
  return { data: _data, count };
}
